import { useQuery } from 'react-query'
import { Text, Button, Header, Loader, HTMLText } from '@crossfit/ui'

import type { Props as FAQModuleProps } from 'components/FAQModule'
import * as contentService from 'services/content'
import { getDateRange } from 'utilities/dates'
import InnerContent from 'components/InnerContent'
import PageHeader from 'components/PageHeader'
import FAQModule from 'components/FAQModule'
import YouTubeVideo from 'components/YouTubeVideo'
import usePageTitle from 'hooks/usePageTitle'
import NotAuthorized from 'components/NotAuthorized'
import style from './style.module.scss'
import { useAffiliate } from 'hooks/useAffiliate'

type Content = {
  headlineText: string
  subheadlineText?: string
  paymentCenterLink: string
  bodyText: string
  faqModule: FAQModuleProps
  upcomingEvents: {
    sectionTitle: string
    affiliateEvents: AffiliateEvents[]
    headlineText: string
  }
  webinars: {
    headlineText: string
    subheadlineText?: string
    videos?: Videos[]
  }
}

type Videos = {
  title: string
  youtubeId: string
}

type Button = {
  text: string
  url: string
}

type AffiliateEvents = {
  cta: {
    text: string
    url: string
  }
  endDateTime: string
  headlineText: string
  locationText: string
  startDateTime: string
  timeZone: string
  type: string
}

function usePricingPage() {
  const { canAccessPricing } = useAffiliate()
  const contentQuery = useQuery(
    'affiliate-pricing',
    () =>
      contentService
        .fetchPath<Content>('/affiliate-pricing/affiliate-pricing-updates')
        .then((content) => {
          if (
            content.upcomingEvents &&
            content.upcomingEvents.affiliateEvents
          ) {
            content.upcomingEvents.affiliateEvents =
              content.upcomingEvents.affiliateEvents.sort(
                (a, b) =>
                  new Date(a.startDateTime).getTime() -
                  new Date(b.startDateTime).getTime(),
              )
          }
          return content
        }),
    { enabled: canAccessPricing },
  )

  return {
    hasAccess: canAccessPricing,
    content: contentQuery.data,
    isLoading: contentQuery.isLoading,
  }
}

function PricingPage() {
  const { content, isLoading, hasAccess } = usePricingPage()
  const { selectedAffiliate: affiliate } = useAffiliate()

  usePageTitle(
    `CrossFit Affiliate Toolkit${content ? ` | ${content.headlineText}` : ''}`,
  )

  if (isLoading) {
    return <Loader />
  }

  if (!hasAccess) {
    return <NotAuthorized />
  }

  return (
    <main className={style.main}>
      <PageHeader subheadlineText={content.subheadlineText}>
        {content.headlineText}
      </PageHeader>
      <InnerContent className={style.innerContent} variant='xlarge'>
        {affiliate.roles.includes('lor') && (
          <HTMLText className={style.bannerContainer}>
            {content.paymentCenterLink}
          </HTMLText>
        )}
        <HTMLText className={style.htmlText}>{content.bodyText}</HTMLText>
        {content.upcomingEvents.affiliateEvents && (
          <>
            <Header as='h3' size='medium' className={style.cardsHeadlineText}>
              {content.upcomingEvents.sectionTitle}
            </Header>
            <div className={style.cardsContainer}>
              <div className={style.cardsHeadlineTextContainer}>
                <Header
                  as='h3'
                  size='small'
                  className={style.cardsHeadlineText}
                >
                  {content.upcomingEvents.headlineText}
                </Header>
              </div>
              <div className={style.cardsList}>
                {content.upcomingEvents.affiliateEvents.map(
                  ({
                    headlineText,
                    locationText,
                    type,
                    startDateTime,
                    endDateTime,
                    cta,
                  }) => (
                    <div className={style.cardContent} key={headlineText}>
                      <div className={style.tag}>{type}</div>
                      <Text size='large' className={style.eventTitle}>
                        {headlineText}
                      </Text>
                      <div className={style.eventDetails}>
                        <Text size='small' className={style.eventDate}>
                          {getDateRange(startDateTime, endDateTime)}
                        </Text>
                        <Text size='small' className={style.location}>
                          {locationText}
                        </Text>
                      </div>
                      {cta && (
                        <div className={style.cta}>
                          <Button
                            as='a'
                            variant='tertiary'
                            href={cta.url}
                            target='_blank'
                            rel='noreferrer'
                          >
                            {cta.text}
                          </Button>
                        </div>
                      )}
                    </div>
                  ),
                )}
              </div>
            </div>
          </>
        )}
        {content.faqModule.questions && (
          <FAQModule {...content.faqModule}></FAQModule>
        )}
      </InnerContent>
      <div className={style.videoContainer}>
        <InnerContent className={style.innerContent} variant='xlarge'>
          <Header as='h3' size='medium' className={style.cardsHeadlineText}>
            {content.webinars.headlineText}
          </Header>

          {!content.webinars.videos && content.webinars.subheadlineText}
          {content.webinars.videos && (
            <>
              {content.webinars.videos.map((video) => (
                <div key={video.youtubeId} className={style.videoList}>
                  <YouTubeVideo id={video.youtubeId} title='View Webinar' />
                </div>
              ))}
            </>
          )}
        </InnerContent>
      </div>
    </main>
  )
}

export default PricingPage
